import React, { memo, useCallback, Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useToggle } from 'react-use';
import { v4 } from 'uuid';
import {
  addToastNotification,
  doPatchBundle,
  getSingleDevice,
  doGetBundle,
  patchDeviceParameters,
  updatePivotOverviewPageSingleSelectedDevice,
  updateBundlesDictionary,
  updateEchoOverviewPageSelectedDevice
} from 'redux/actions';
import { mapDeviceSearchResult } from 'apis/SearchDevices';
import BundleOverviewControllerDeviceNameEditor from '../BundleOverviewControllerDeviceNameEditor/BundleOverviewControllerDeviceNameEditor';
import { EditIcon, ErrorSolidIcon } from 'components/icons';
import IconButton from 'components/shared/IconButton';
import { ProgressIndicator } from 'components/shared/Progress';

const BundleOverviewControllerDeviceName = ({ deviceType, isGa2, device, bundle }) => {
  const dispatch = useDispatch();
  const [editMode, toggleEditMode] = useToggle(false);
  const [editError, setEditError] = useState(null);
  const [isPending, setIsPending] = useState(false);

  const isPivotHub = deviceType === 'pivot';
  const isGa2OrEcho = isGa2 || deviceType === 'echo';
  const deviceNameLabel =
    isPivotHub && isGa2
      ? device?.tags?.userLabel || '(No name)'
      : isPivotHub
      ? bundle?.name || 'Unknown'
      : device?.tags?.userLabel || device?.deviceId || device?.id || 'Unknown';

  const onSuccess = useCallback(() => {
    const isEcho = deviceType === 'echo';

    if (isGa2OrEcho) {
      dispatch(
        getSingleDevice(device?.deviceId, (result) => {
          isEcho
            ? dispatch(updateEchoOverviewPageSelectedDevice(result))
            : dispatch(updatePivotOverviewPageSingleSelectedDevice(result));
        })
      );
    } else {
      dispatch(
        doGetBundle(bundle?.bundleId, (result) => {
          result['bundleId'] = bundle?.bundleId;

          const data = mapDeviceSearchResult({ bundles: [result] });

          dispatch(updateBundlesDictionary(data.bundles));
        })
      );
    }
    dispatch(
      addToastNotification({
        id: v4(),
        description: 'Updated device name successfully.'
      })
    );
    setIsPending(false);
  }, [dispatch, device, bundle, isGa2OrEcho, deviceType]);

  const onError = useCallback(
    (error) => {
      dispatch(
        addToastNotification({
          id: v4(),
          description: 'Failed to update device name.'
        })
      );
      setIsPending(false);
    },
    [dispatch]
  );

  const updateDeviceName = useCallback(
    (userLabel) => {
      device?.id && dispatch(patchDeviceParameters(device.id, { tags: { userLabel } }, device, onError, onSuccess));
    },
    [dispatch, onError, onSuccess, device]
  );

  const updateBundleName = useCallback(
    (name) => {
      bundle?.bundleId && dispatch(doPatchBundle(bundle.bundleId, { name }, onSuccess, onError));
    },
    [bundle?.bundleId, dispatch, onError, onSuccess]
  );

  const handleOnSubmit = useCallback(
    ({ name }) => {
      setIsPending(true);

      const trimmedName = name.trim();
      isGa2OrEcho ? updateDeviceName(trimmedName) : updateBundleName(trimmedName);
      toggleEditMode(false);
    },
    [updateDeviceName, updateBundleName, toggleEditMode, isGa2OrEcho]
  );

  const handleOnCancel = useCallback(() => {
    toggleEditMode(false);
    setEditError(null);
  }, [toggleEditMode]);

  const handleOnError = useCallback((error) => {
    setEditError(error);
  }, []);

  return (
    <div id="BundleOverviewControllerHubDetail_DeviceName" className="BundleOverviewControllerHubDetail_DeviceName">
      <div
        id="BundleOverviewControllerHubDetailTitle_Container"
        className="BundleOverviewControllerHubDetailTitle_Container"
      >
        {isPending && <ProgressIndicator />}
        <div id="BundleOverviewControllerHubDetailTitle" className="BundleOverviewControllerHubDetailTitle">
          Device Name
        </div>

        {Object.keys(editError || {}).length > 0 ? (
          <div className="BundleOverviewControllerHubDetailError_Container">
            <ErrorSolidIcon className="BundleOverviewControllerHubDetailError_Icon" />
            <div className="BundleOverviewControllerHubDetailError_Message" data-tip={editError.name.message}>
              {editError.name.message}
            </div>
          </div>
        ) : null}
      </div>
      <div className="BundleOverviewControllerDeviceName_Container">
        {editMode ? (
          <BundleOverviewControllerDeviceNameEditor
            name={deviceNameLabel}
            onSubmit={handleOnSubmit}
            onCancel={handleOnCancel}
            onError={handleOnError}
          />
        ) : (
          <Fragment>
            <div
              id="BundleOverviewControllerDeviceName"
              className="BundleOverviewControllerDeviceName"
              data-tip={deviceNameLabel}
            >
              {deviceNameLabel}
            </div>
            <IconButton
              aria-label="Edit"
              variant="link"
              color="secondary"
              size="x-small"
              onClick={() => toggleEditMode(true)}
            >
              <EditIcon />
            </IconButton>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default memo(BundleOverviewControllerDeviceName);
